import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ToggleButton } from 'primereact/togglebutton';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MailSettingsDto, MailSettingsService, MailSettingsType } from '../services/MailSettingsService';

const TYPE_OPTIONS: { label: string; value: MailSettingsType }[] = [
    { label: 'System (ENV)', value: 'SYSTEM' },
    { label: 'Normal (Username / Password)', value: 'NORMAL' },
    { label: 'Google (Gmail OAuth2)', value: 'GOOGLE' },
    { label: 'Microsoft (Office 365 OAuth2)', value: 'MICROSOFT' },
];

export const MailSettings: React.FC = () => {
    const { t } = useTranslation();
    const toast = useRef<Toast>(null);

    const [settings, setSettings] = useState<MailSettingsDto>({ type: 'SYSTEM' });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [authorizing, setAuthorizing] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [testing, setTesting] = useState(false);

    // -----------------------------------------------------------------------
    // Load settings
    // -----------------------------------------------------------------------
    const loadSettings = useCallback(async () => {
        try {
            const data = await MailSettingsService.getSettings();
            setSettings(data);
        } catch {
            toast.current?.show({
                severity: 'error',
                summary: t('mailSettings.loadError'),
                life: 4000,
            });
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    // -----------------------------------------------------------------------
    // Handle OAuth2 callback redirect
    //   The backend redirects to /mail-settings?oauth=success|error&provider=…
    // -----------------------------------------------------------------------
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const oauth    = params.get('oauth');
        const provider = params.get('provider') ?? '';

        if (oauth === 'success') {
            toast.current?.show({
                severity: 'success',
                summary: t('mailSettings.oauthSuccess', { provider }),
                life: 4000,
            });
            // Clean the URL so a refresh doesn't re-show the message
            window.history.replaceState({}, '', window.location.pathname);
            loadSettings();
        } else if (oauth === 'error') {
            toast.current?.show({
                severity: 'error',
                summary: t('mailSettings.oauthError', { provider }),
                life: 6000,
            });
            window.history.replaceState({}, '', window.location.pathname);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // -----------------------------------------------------------------------
    // Field helpers
    // -----------------------------------------------------------------------
    const set = <K extends keyof MailSettingsDto>(key: K, value: MailSettingsDto[K]) =>
        setSettings(prev => ({ ...prev, [key]: value }));

    const isOAuth = settings.type === 'GOOGLE' || settings.type === 'MICROSOFT';

    // -----------------------------------------------------------------------
    // Save
    // -----------------------------------------------------------------------
    const handleSave = async () => {
        setSaving(true);
        try {
            const updated = await MailSettingsService.saveSettings(settings);
            setSettings(updated);
            toast.current?.show({
                severity: 'success',
                summary: t('mailSettings.saveSuccess'),
                life: 2000,
            });
        } catch {
            toast.current?.show({
                severity: 'error',
                summary: t('mailSettings.saveError'),
                life: 4000,
            });
        } finally {
            setSaving(false);
        }
    };

    // -----------------------------------------------------------------------
    // OAuth2 authorize
    // -----------------------------------------------------------------------
    const handleAuthorize = async (provider: 'google' | 'microsoft') => {
        // Persist current form data first so the backend reads the latest credentials
        setSaving(true);
        try {
            const updated = await MailSettingsService.saveSettings(settings);
            setSettings(updated);
        } catch {
            toast.current?.show({
                severity: 'error',
                summary: t('mailSettings.saveError'),
                life: 4000,
            });
            setSaving(false);
            return;
        }
        setSaving(false);

        setAuthorizing(true);
        try {
            const authUrl = await MailSettingsService.getAuthorizationUrl(provider);
            // Navigate the current tab; the backend callback will redirect back
            window.location.href = authUrl;
        } catch {
            toast.current?.show({
                severity: 'error',
                summary: t('mailSettings.authorizeError'),
                life: 4000,
            });
            setAuthorizing(false);
        }
    };

    // -----------------------------------------------------------------------
    // Send test email
    // -----------------------------------------------------------------------
    const handleTest = async () => {
        if (!testEmail) return;
        setTesting(true);
        try {
            await MailSettingsService.sendTestEmail(testEmail);
            toast.current?.show({
                severity: 'success',
                summary: t('mailSettings.testSuccess', { to: testEmail }),
                life: 4000,
            });
        } catch {
            toast.current?.show({
                severity: 'error',
                summary: t('mailSettings.testError'),
                life: 4000,
            });
        } finally {
            setTesting(false);
        }
    };

    // -----------------------------------------------------------------------
    // Render helpers
    // -----------------------------------------------------------------------
    const SetTag: React.FC = () => (
        <Tag value={t('mailSettings.set')} severity="success" className="ml-2" />
    );

    const fieldClass = 'p-field mt-3';
    const labelClass = 'block mb-1 font-semibold';

    if (loading) {
        return <Card style={{ margin: '2em' }}><p>{t('loading')}</p></Card>;
    }

    return (
        <>
            <Toast ref={toast} />
            <Card style={{ margin: '2em' }} title={t('mailSettings.title')}>
                <div className="p-fluid">

                    {/* Type selector */}
                    <div className={fieldClass}>
                        <label className={labelClass}>{t('mailSettings.type')}</label>
                        <Dropdown
                            value={settings.type}
                            options={TYPE_OPTIONS}
                            onChange={e => set('type', e.value)}
                            placeholder={t('mailSettings.typePlaceholder')}
                        />
                    </div>

                    {/* SYSTEM: no extra configuration needed */}
                    {settings.type === 'SYSTEM' && (
                        <p className="mt-3 text-color-secondary">
                            {t('mailSettings.systemDescription')}
                        </p>
                    )}

                    {/* ---- Fields common to NORMAL / GOOGLE / MICROSOFT ---- */}
                    {settings.type && settings.type !== 'SYSTEM' && (
                        <>
                            <Divider />

                            <div className={fieldClass}>
                                <label className={labelClass}>{t('mailSettings.from')}</label>
                                <InputText
                                    value={settings.from ?? ''}
                                    onChange={e => set('from', e.target.value)}
                                    placeholder="no-reply@example.com"
                                />
                            </div>

                            <div className={fieldClass}>
                                <label className={labelClass}>{t('mailSettings.username')}</label>
                                <InputText
                                    value={settings.username ?? ''}
                                    onChange={e => set('username', e.target.value)}
                                />
                            </div>

                            {/* NORMAL: host / port / TLS */}
                            {settings.type === 'NORMAL' && (
                                <>
                                    <div className={fieldClass}>
                                        <label className={labelClass}>{t('mailSettings.host')}</label>
                                        <InputText
                                            value={settings.host ?? ''}
                                            onChange={e => set('host', e.target.value)}
                                            placeholder="smtp.example.com"
                                        />
                                    </div>

                                    <div className={fieldClass}>
                                        <label className={labelClass}>{t('mailSettings.port')}</label>
                                        <InputNumber
                                            value={settings.port ?? null}
                                            onValueChange={e => set('port', e.value ?? undefined)}
                                            useGrouping={false}
                                            min={1}
                                            max={65535}
                                            placeholder="465"
                                        />
                                    </div>

                                    <div className={fieldClass}>
                                        <label className={labelClass}>{t('mailSettings.tls')}</label>
                                        <ToggleButton
                                            checked={settings.tls ?? false}
                                            onChange={e => set('tls', e.value)}
                                            onLabel={t('mailSettings.tlsOn')}
                                            offLabel={t('mailSettings.tlsOff')}
                                        />
                                    </div>

                                    <div className={fieldClass}>
                                        <label className={labelClass}>
                                            {t('mailSettings.password')}
                                            {settings.passwordSet && <SetTag />}
                                        </label>
                                        <Password
                                            value={settings.password ?? ''}
                                            onChange={e => set('password', e.target.value)}
                                            placeholder={
                                                settings.passwordSet
                                                    ? t('mailSettings.leaveBlankToKeep')
                                                    : ''
                                            }
                                            feedback={false}
                                            toggleMask
                                        />
                                    </div>
                                </>
                            )}

                            {/* GOOGLE / MICROSOFT: OAuth2 fields */}
                            {isOAuth && (
                                <>
                                    <div className={fieldClass}>
                                        <label className={labelClass}>{t('mailSettings.clientId')}</label>
                                        <InputText
                                            value={settings.clientId ?? ''}
                                            onChange={e => set('clientId', e.target.value)}
                                        />
                                    </div>

                                    <div className={fieldClass}>
                                        <label className={labelClass}>
                                            {t('mailSettings.clientSecret')}
                                            {settings.clientSecretSet && <SetTag />}
                                        </label>
                                        <Password
                                            value={settings.clientSecret ?? ''}
                                            onChange={e => set('clientSecret', e.target.value)}
                                            placeholder={
                                                settings.clientSecretSet
                                                    ? t('mailSettings.leaveBlankToKeep')
                                                    : ''
                                            }
                                            feedback={false}
                                            toggleMask
                                        />
                                    </div>

                                    {/* Microsoft-only: tenant ID */}
                                    {settings.type === 'MICROSOFT' && (
                                        <div className={fieldClass}>
                                            <label className={labelClass}>{t('mailSettings.tenantId')}</label>
                                            <InputText
                                                value={settings.tenantId ?? ''}
                                                onChange={e => set('tenantId', e.target.value)}
                                                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                            />
                                        </div>
                                    )}

                                    {/* Refresh token status */}
                                    <div className={fieldClass}>
                                        <label className={labelClass}>{t('mailSettings.refreshToken')}</label>
                                        {settings.refreshTokenSet ? (
                                            <Tag
                                                value={t('mailSettings.set')}
                                                severity="success"
                                            />
                                        ) : (
                                            <Tag
                                                value={t('mailSettings.notSet')}
                                                severity="warning"
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {/* ---- Test email ---- */}
                    <Divider />
                    <div className={fieldClass}>
                        <label className={labelClass}>{t('mailSettings.testEmail')}</label>
                        <div className="p-inputgroup">
                            <InputText
                                value={testEmail}
                                onChange={e => setTestEmail(e.target.value)}
                                placeholder={t('mailSettings.testEmailPlaceholder')}
                                keyfilter="email"
                            />
                            <Button
                                label={t('mailSettings.test')}
                                icon="pi pi-send"
                                onClick={handleTest}
                                loading={testing}
                                disabled={!testEmail}
                            />
                        </div>
                    </div>

                    {/* ---- Action buttons ---- */}
                    <div className="p-fluid grid mt-4">
                        <div className="col-12 md:col-4">
                            <Button
                                label={t('mailSettings.save')}
                                icon="pi pi-save"
                                onClick={handleSave}
                                loading={saving}
                            />
                        </div>

                        {settings.type === 'GOOGLE' && (
                            <div className="col-12 md:col-4">
                                <Button
                                    label={t('mailSettings.authorizeGoogle')}
                                    icon="pi pi-google"
                                    severity="secondary"
                                    onClick={() => handleAuthorize('google')}
                                    loading={authorizing}
                                    disabled={!settings.clientId || !settings.clientSecretSet}
                                    tooltip={
                                        !settings.clientSecretSet
                                            ? t('mailSettings.saveSecretFirst')
                                            : undefined
                                    }
                                />
                            </div>
                        )}

                        {settings.type === 'MICROSOFT' && (
                            <div className="col-12 md:col-4">
                                <Button
                                    label={t('mailSettings.authorizeMicrosoft')}
                                    icon="pi pi-microsoft"
                                    severity="secondary"
                                    onClick={() => handleAuthorize('microsoft')}
                                    loading={authorizing}
                                    disabled={!settings.clientId || !settings.tenantId}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </Card>
        </>
    );
};

export default MailSettings;
